import { DOCUMENT } from '@angular/common';
import { Component, OnDestroy, OnInit, Renderer2, inject } from '@angular/core';
import { ActiveTab, TabService } from '../../services/tab-service.service';
import { LanguageService } from '../../services/language.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-legal-faq',
  templateUrl: './legal-faq.component.html',
  styleUrl: './legal-faq.component.scss'
})
export class LegalFaqComponent implements OnInit, OnDestroy {
  private _tabService: TabService = inject(TabService);
  private _languageService: LanguageService = inject(LanguageService);
  private _document: Document = inject(DOCUMENT);
  private _renderer: Renderer2 = inject(Renderer2);

  ACTIVE_TAB = ActiveTab;
  activeTab!: ActiveTab;
  currentLang!: string;

  private subscriptions = new Subscription();
  private faqSchemaScriptEl?: HTMLScriptElement;
  private trackedScrollDepths = new Set<number>();
  private onWindowScroll = () => this.trackScrollDepth();

  constructor() {}

  ngOnInit(): void {
    this.activeTabSubscription();
    this.currentLang = this._languageService.currentLang;
    this.updateSeoForCurrentLanguage();
    this.updateFaqSchema();
    window.addEventListener('scroll', this.onWindowScroll, { passive: true });
    this.languageSubscription();
  }

  languageSubscription(): void {
    const subscription = this._languageService.onLangChange$.subscribe(() => {
      this.currentLang = this._languageService.currentLang;
      this.updateSeoForCurrentLanguage();
      this.updateFaqSchema();
    });
    this.subscriptions.add(subscription);
  }

  activeTabSubscription(): void {
    const subscription = this._tabService.activeTab$.subscribe(tab => {
      this.activeTab = tab;
    });
    this.subscriptions.add(subscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    window.removeEventListener('scroll', this.onWindowScroll);
    this.removeFaqSchema();
  }

  private updateSeoForCurrentLanguage(): void {
    const isNo = this.currentLang === 'no';
    const title = isNo
      ? 'Henter Pant FAQ – Slik samler lag inn penger med pant'
      : 'Henter Pant FAQ – How to raise money for sports teams with bottle deposits';
    const description = isNo
      ? 'Lær hvordan Henter Pant fungerer. Opprett lag, registrer utøvere, koble foresatte og tjen penger på pant – uten salg og dugnadspress.'
      : 'Learn how Henter Pant works. Create a team, register athletes, connect parents, and start raising money through bottle deposit pickups – without selling products.';

    this._document.title = title;
    this.setOrCreateMetaTag('description', description);
  }

  private updateFaqSchema(): void {
    this.removeFaqSchema();

    const schema = this.currentLang === 'no' ? this.getNoFaqSchema() : this.getEnFaqSchema();
    const scriptEl = this._renderer.createElement('script') as HTMLScriptElement;
    scriptEl.type = 'application/ld+json';
    scriptEl.text = JSON.stringify(schema);
    this._renderer.appendChild(this._document.head, scriptEl);
    this.faqSchemaScriptEl = scriptEl;
  }

  private removeFaqSchema(): void {
    if (!this.faqSchemaScriptEl) return;
    this._renderer.removeChild(this._document.head, this.faqSchemaScriptEl);
    this.faqSchemaScriptEl = undefined;
  }

  private setOrCreateMetaTag(name: string, content: string): void {
    const existingTag = this._document.head.querySelector(`meta[name="${name}"]`) as HTMLMetaElement | null;
    let tag: HTMLMetaElement;
    if (!existingTag) {
      tag = this._renderer.createElement('meta');
      tag.setAttribute('name', name);
      this._renderer.appendChild(this._document.head, tag);
    } else {
      tag = existingTag;
    }
    tag.setAttribute('content', content);
  }

  private trackScrollDepth(): void {
    const scrollTop = window.scrollY || this._document.documentElement.scrollTop || 0;
    const viewportHeight = window.innerHeight || this._document.documentElement.clientHeight || 0;
    const fullHeight = this._document.documentElement.scrollHeight || 0;
    const maxScrollable = Math.max(fullHeight - viewportHeight, 1);
    const ratio = Math.min((scrollTop / maxScrollable) * 100, 100);
    const milestones = [25, 50, 75, 100];

    for (const milestone of milestones) {
      if (ratio >= milestone && !this.trackedScrollDepths.has(milestone)) {
        this.trackedScrollDepths.add(milestone);
        this.pushTrackingEvent('faq_scroll_depth', { percent: milestone, lang: this.currentLang });
      }
    }
  }

  private pushTrackingEvent(event: string, payload: Record<string, unknown>): void {
    const win = window as Window & { dataLayer?: unknown[] };
    if (!Array.isArray(win.dataLayer)) return;
    win.dataLayer.push({ event, ...payload });
  }

  private getNoFaqSchema() {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: 'no',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'Hvordan oppretter jeg et lag i Henter Pant?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'For å opprette lagprofil, gå til Sign up-siden: https://gipant.no/no/henter-pant/profile?tab=sign-up. Når laget er opprettet, finner du invitasjonskoden i appen. Derfra kan du dele koden med utøvere og foresatte slik at de kan bli med i laget.'
          }
        },
        {
          '@type': 'Question',
          name: 'Hvordan registrerer jeg en utøver?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Utøveren velger rollen utøver, registrerer navn og mobilnummer, og bruker invitasjonskoden fra laget. Dette kobler utøveren til riktig lag.'
          }
        },
        {
          '@type': 'Question',
          name: 'Hvordan kobler jeg foresatt til en utøver?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Foresatt oppretter eller logger inn på en foresattprofil og kobler seg til utøveren med telefonnummer. Foresatt får full oversikt og kan følge opp aktiviteten.'
          }
        },
        {
          '@type': 'Question',
          name: 'Hvordan fungerer invitasjonskoden?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Invitasjonskoden sikrer at utøvere kobles til riktig lag. Den deles av lagleder og brukes ved registrering.'
          }
        },
        {
          '@type': 'Question',
          name: 'Hvordan kan vi øke lagets inntekter?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Del profiler aktivt, svar raskt på forespørsler, vær tilgjengelig og følg opp givere. Mange små bidrag gir stabile inntekter over tid.'
          }
        }
      ]
    };
  }

  private getEnFaqSchema() {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      inLanguage: 'en',
      mainEntity: [
        {
          '@type': 'Question',
          name: 'How do I create a team in Henter Pant?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'To create a team profile, go to the Sign up page: https://gipant.no/en/henter-pant/profile?tab=sign-up. Once the team is created, you can find the invitation code in the app. From there, share the code with athletes and parents so they can join the team.'
          }
        },
        {
          '@type': 'Question',
          name: 'How do I register an athlete?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The athlete selects the role Player in the app, enters their name and mobile number, and uses the invitation code from the team leader. This connects the athlete to the correct team in Henter Pant.'
          }
        },
        {
          '@type': 'Question',
          name: 'How do I connect a parent to an athlete?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The parent creates or logs into a parent profile and connects to the athlete profile using the athlete phone number. The parent then gets full visibility into activity and earnings and can manage everything on behalf of the athlete.'
          }
        },
        {
          '@type': 'Question',
          name: 'How does the invitation code work?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'The invitation code ensures that athletes join the correct team during registration. It is shared by the team leader and used to connect users to the team securely and correctly.'
          }
        },
        {
          '@type': 'Question',
          name: 'How can we increase our team’s income?',
          acceptedAnswer: {
            '@type': 'Answer',
            text: 'Share profiles actively within your network, respond quickly to pickup requests, stay available, and follow up repeat supporters over time. Many small contributions over time create stable income.'
          }
        }
      ]
    };
  }
}

